'use client';

import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/nextjs';
import { io, Socket } from 'socket.io-client';
import { apiFetchAuthed } from './api';
import { ENV_PREFIX } from './query-keys';

export interface Notification {
  id: string;
  clerkUserId: string;
  email?: string;
  type: string;
  category: string;
  priority: string;
  title: string;
  body: string;
  imageUrl?: string;
  actionUrl?: string;
  eventId?: string;
  postId?: string;
  rsvpId?: string;
  channels: string[];
  emailSentAt?: Date;
  pushSentAt?: Date;
  smsSentAt?: Date;
  readAt?: Date;
  clickedAt?: Date;
  deletedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface NotificationPreference {
  id: string;
  clerkUserId: string;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  rsvpEmail: boolean;
  paymentEmail: boolean;
  eventUpdateEmail: boolean;
  reminderEmail: boolean;
  postEmail: boolean;
  adminReviewEmail: boolean;
  waitlistEmail: boolean;
  systemEmail: boolean;
  rsvpPush: boolean;
  paymentPush: boolean;
  eventUpdatePush: boolean;
  reminderPush: boolean;
  postPush: boolean;
  adminReviewPush: boolean;
  waitlistPush: boolean;
  systemPush: boolean;
  rsvpSms: boolean;
  paymentSms: boolean;
  eventUpdateSms: boolean;
  reminderSms: boolean;
  waitlistSms: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  quietHoursTimezone?: string;
  createdAt: Date;
  updatedAt: Date;
}

export function useNotifications() {
  const { getToken, userId } = useAuth();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications
  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery<{ notifications: Notification[]; total: number }>({
    queryKey: [ENV_PREFIX, 'notifications'],
    queryFn: async () => {
      return await apiFetchAuthed<{ notifications: Notification[]; total: number }>('/notifications', {}, getToken);
    },
    enabled: !!userId,
    refetchOnWindowFocus: true,
  });

  // Fetch unread count
  const { data: unreadCountData } = useQuery<{ count: number }>({
    queryKey: [ENV_PREFIX, 'notifications', 'unread-count'],
    queryFn: async () => {
      return await apiFetchAuthed<{ count: number }>('/notifications/unread-count', {}, getToken);
    },
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds as backup
  });

  // Update unread count when data changes
  useEffect(() => {
    if (unreadCountData?.count !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUnreadCount(unreadCountData.count);
    }
  }, [unreadCountData]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return await apiFetchAuthed(
        `/notifications/${notificationId}/read`,
        { method: 'PUT' },
        getToken,
      );
    },
    onMutate: async (notificationId) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: [ENV_PREFIX, 'notifications'] });
      const previous = queryClient.getQueryData([ENV_PREFIX, 'notifications']);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData([ENV_PREFIX, 'notifications'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications.map((n: Notification) =>
            n.id === notificationId ? { ...n, readAt: new Date() } : n
          ),
        };
      });

      setUnreadCount((prev) => Math.max(0, prev - 1));

      return { previous };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err, notificationId, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData([ENV_PREFIX, 'notifications'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications'] });
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications', 'unread-count'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      return await apiFetchAuthed(
        '/notifications/mark-all-read',
        { method: 'PUT' },
        getToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications'] });
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications', 'unread-count'] });
      setUnreadCount(0);
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return await apiFetchAuthed(
        `/notifications/${notificationId}`,
        { method: 'DELETE' },
        getToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications'] });
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications', 'unread-count'] });
    },
  });

  // WebSocket connection
  useEffect(() => {
    if (!userId) return;

    let socketInstance: Socket | null = null;
    let isMounted = true;

    const getFreshToken = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return await (getToken as any)({ skipCache: true });
      } catch {
        return await getToken();
      }
    };

    const connectSocket = async () => {
      try {
        const token = await getFreshToken();
        if (!token || !isMounted) return;

        const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

        socketInstance = io(`${apiUrl}/notifications`, {
          auth: (cb) => {
            void getFreshToken().then((freshToken) => cb({ token: freshToken }));
          },
          transports: ['websocket', 'polling'],
        });

        // Check if unmounted during connection setup
        if (!isMounted) {
          socketInstance.disconnect();
          return;
        }

        socketInstance.io.on('reconnect_attempt', async () => {
          const freshToken = await getFreshToken();
          if (freshToken && socketInstance && isMounted) {
            socketInstance.auth = { token: freshToken };
          }
        });

        socketInstance.on('connect', () => {
          if (!isMounted) return;
          console.log('✅ Notifications WebSocket connected');
          setIsConnected(true);
        });

        socketInstance.on('connected', (data) => {
          if (!isMounted) return;
          console.log('Notification service ready:', data);
        });

        socketInstance.on('disconnect', () => {
          if (!isMounted) return;
          console.log('❌ Notifications WebSocket disconnected');
          setIsConnected(false);
        });

        socketInstance.on('notification:new', (notification: Notification) => {
          if (!isMounted) return;
          console.log('📬 New notification received:', notification);

          // Update notifications list
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          queryClient.setQueryData([ENV_PREFIX, 'notifications'], (old: any) => {
            if (!old) return { notifications: [notification], total: 1 };
            return {
              ...old,
              notifications: [notification, ...old.notifications],
              total: old.total + 1,
            };
          });

          // Increment unread count
          setUnreadCount((prev) => prev + 1);

          // Show browser notification if supported
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.body,
              icon: notification.imageUrl || '/icon.png',
              badge: '/badge.png',
            });
          }
        });

        socketInstance.on('notification:unread_count', ({ count }: { count: number }) => {
          if (!isMounted) return;
          setUnreadCount(count);
          queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notifications', 'unread-count'] });
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socketInstance.on('error', (error: any) => {
          console.error('WebSocket error:', error);
        });

        if (isMounted) {
          setSocket(socketInstance);
        }
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
      }
    };

    void connectSocket();

    return () => {
      isMounted = false;
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [userId, getToken, queryClient]);

  const markAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation]
  );

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback(
    (notificationId: string) => {
      deleteNotificationMutation.mutate(notificationId);
    },
    [deleteNotificationMutation]
  );
  const notifications = Array.isArray(notificationsData?.notifications)
    ? notificationsData.notifications
    : [];

  return {
    notifications,
    total: notificationsData?.total || 0,
    unreadCount,
    isLoading,
    error,
    isConnected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch,
  };
}

// Fetch notification preferences
export function useNotificationPreferences() {
  const { userId, getToken } = useAuth();

  return useQuery<NotificationPreference>({
    queryKey: [ENV_PREFIX, 'notification-preferences'],
    queryFn: async () => {
      return await apiFetchAuthed('/notifications/preferences', {}, getToken);
    },
    enabled: !!userId,
  });
}

// Update notification preferences
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (preferences: Partial<NotificationPreference>) => {
      return await apiFetchAuthed(
        '/notifications/preferences',
        {
          method: 'PUT',
          body: JSON.stringify(preferences),
        },
        getToken,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ENV_PREFIX, 'notification-preferences'] });
    },
  });
}
